"use client";

import React, { forwardRef } from "react";
import NextLink from "next/link";
import {
  Link as ReactLink,
  type LinkProps as SdkLinkProps,
} from "@sitecore-content-sdk/nextjs";

const SUPPORTED_NEXT_LINK_PROPS = [
  "as",
  "onNavigate",
  "passHref",
  "prefetch",
  "replace",
  "scroll",
  "shallow",
] as const;

const FILE_EXTENSION_MATCHER = /^\/.*\.\w+$/;

const sanitizeReactLinkProps = (props: SdkLinkProps): SdkLinkProps => {
  const sanitized = { ...props } as Record<string, unknown>;
  for (const key of [...SUPPORTED_NEXT_LINK_PROPS, "internalLinkMatcher"]) {
    delete sanitized[key];
  }
  return sanitized as unknown as SdkLinkProps;
};

/**
 * Drop-in replacement for `@sitecore-content-sdk/nextjs` `Link`.
 *
 * Mirrors the SDK 2.0.2 logic except it does NOT pass `locale={false}` to
 * `next/link`. In Next.js 16 App Router that prop is no longer consumed by
 * NextLink and is forwarded to the underlying `<a>`, producing a React
 * non-boolean-attribute warning.
 */
export const SitecoreLink = forwardRef<HTMLAnchorElement, SdkLinkProps>(
  (props, ref) => {
    const {
      field,
      editable = true,
      children,
      internalLinkMatcher = /^\//g,
      showLinkTextWithChildrenPresent,
      ...rest
    } = props;

    const fieldAsAny = field as unknown as {
      value?: {
        href?: string;
        querystring?: string;
        anchor?: string;
        title?: string;
        target?: string;
        class?: string;
        text?: string;
      };
      href?: string;
      querystring?: string;
      anchor?: string;
      title?: string;
      target?: string;
      class?: string;
      text?: string;
      metadata?: unknown;
    };

    const resetScroll = () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((field?.value as any)?.target == "_blank") return;
      if (!anchor && typeof window !== "undefined")
        window.scroll({
          top: 0,
          behavior: "smooth",
        });
    };

    if (
      !fieldAsAny ||
      (!fieldAsAny.value && !fieldAsAny.href && !fieldAsAny.metadata)
    ) {
      return null;
    }

    const value = fieldAsAny.href ? fieldAsAny : fieldAsAny.value;
    const { href, querystring, anchor } = value || {};
    const isEditing = editable && fieldAsAny.metadata;

    if (href && !isEditing) {
      const tc = value?.text ? value?.text : (value as { href?: string })?.href;
      const text = showLinkTextWithChildrenPresent || !children ? tc : null;
      const isMatching = internalLinkMatcher.test(href);
      const isFileUrl = FILE_EXTENSION_MATCHER.test(href);

      if (isMatching && !isFileUrl) {
        return (
          <NextLink
            onClick={resetScroll}
            href={{ pathname: href, query: querystring, hash: anchor }}
            title={value?.title}
            target={value?.target}
            className={value?.class}
            ref={ref}
            {...(rest as Record<string, unknown>)}
          >
            {text}
            {children}
          </NextLink>
        );
      }
    }

    const reactLinkProps = sanitizeReactLinkProps(props);
    return <ReactLink {...reactLinkProps} ref={ref} />;
  },
);

SitecoreLink.displayName = "SitecoreLink";
